import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import api from '@/services/api';
import { useUser } from '@/contexts/UserContext';
import type { UserRole } from '@/types/user';
import { asianCountriesFallback, type Country as FallbackCountry } from '@/data/countries';

interface ValidationError {
  field: string;
  message: string;
}

interface SignUpResponse {
  status: string;
  message: string;
  data?: {
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      mobile_number: string;
      mobile_region_code: string;
      dob: string;
      gender: string;
      country: string;
      fk_role_id: number;
      role: UserRole;
    };
    reset_token?: string;
  };
}

interface ApiCountry {
  code: string;
  label: string;
  region: string;
  phone_code?: string;
  phone_codes?: string[];  // Array of phone codes
}

interface CountryWithPhoneCode {
  code: string;
  label: string;
  region: string;
  phone_code: string;
  phone_codes: string[];
}

interface CountriesApiResponse {
  status: string;
  message: string;
  data: ApiCountry[];
}

interface Country {
  code: string;
  name: string;
  phone_code: string;
}

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_region_code: '+60',
    mobile_number: '',
    dob: '',
    gender: '',
    country: 'Malaysia',
    password: '',
    password_confirmation: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [apiMessage, setApiMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countries, setCountries] = useState<CountryWithPhoneCode[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  
  const navigate = useNavigate();
  const { logedin } = useUser();

  // Password validation
  const passwordValidation = {
    hasMinLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)
  };

  const isValidPassword = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = formData.password === formData.password_confirmation;
  const homePath = import.meta.env.VITE_APP_HOME || '/';
  const policyPath = import.meta.env.VITE_APP_POLICY || "/policy";
  const termsPath = import.meta.env.VITE_APP_TERMS || "/terms";

  useEffect(() => {
    // Load countries from API
    const loadCountries = async () => {
      setIsLoadingCountries(true);
      try {
        // Fetch countries from API
        const response = await api.get<CountriesApiResponse>('/v1/options/countries?region=Asia');
        
        if (response.data.status === 'success' && response.data.data) {
          // Map API countries to include phone codes from fallback
          const apiCountries = response.data.data.map((apiCountry: ApiCountry) => {
            // Find matching country in fallback to get phone code
            // const fallbackCountry = asianCountriesFallback.find(
            //   fallback => fallback.code === apiCountry.code
            // );
            const defaultPhoneCode = getDefaultPhoneCode(apiCountry.code);
            
            return {
              code: apiCountry.code,
              label: apiCountry.label,
              region: apiCountry.region,
              phone_code: apiCountry.phone_code || defaultPhoneCode,
              phone_codes: apiCountry.phone_codes ? apiCountry.phone_codes.filter((code): code is string => code !== undefined) : [defaultPhoneCode],
            };
          });
          
          setCountries(apiCountries);
        } else {
          // If API fails, use fallback
          console.warn('API returned unsuccessful response, using fallback countries');
          setCountries(asianCountriesFallback as CountryWithPhoneCode[]);
        }
      } catch (error) {
        console.error('Failed to load countries from API, using fallback:', error);
        // Use fallback data if API fails
        setCountries(asianCountriesFallback as CountryWithPhoneCode[]);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    loadCountries();

    // Add auth background
    const root = document.querySelector('#root');
    root?.classList.add('auth-background');

    return () => {
      root?.classList.remove('auth-background');
    };
  }, []);

  // Update mobile_region_code when countries are loaded and country is already selected
  useEffect(() => {
    if (countries.length > 0 && formData.country) {
      const selectedCountry = countries.find(c => c.label === formData.country);
      if (selectedCountry && selectedCountry.phone_code && formData.mobile_region_code === '+60') {
        // Only update if still using default value, to avoid overwriting user selection
        setFormData(prev => ({
          ...prev,
          mobile_region_code: selectedCountry.phone_code
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]); // Only run when countries load, handleCountryChange handles country changes

  // Helper function to get default phone code for a country
  const getDefaultPhoneCode = (countryCode: string): string => {
    const defaultPhoneCodes: Record<string, string> = {
      'MY': '+60', // Malaysia
      'SG': '+65', // Singapore
      'ID': '+62', // Indonesia
      'TH': '+66', // Thailand
      'CN': '+86', // China
      'IN': '+91', // India
      'JP': '+81', // Japan
      'KR': '+82', // South Korea
      'PH': '+63', // Philippines
      'VN': '+84', // Vietnam
      'US': '+1',  // United States
    };
    
    return defaultPhoneCodes[countryCode] || '+1';
  };

  const getFieldError = (fieldName: string) => {
    return errors.find(error => error.field === fieldName)?.message;
  };

  const clearFieldError = (fieldName: string) => {
    setErrors(prev => prev.filter(error => error.field !== fieldName));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationError[] = [];

    // Name validation
    if (!formData.first_name.trim()) {
      newErrors.push({ field: 'first_name', message: 'First name is required' });
    }

    if (!formData.last_name.trim()) {
      newErrors.push({ field: 'last_name', message: 'Last name is required' });
    }

    // Email validation
    if (!formData.email) {
      newErrors.push({ field: 'email', message: 'Email is required' });
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.push({ field: 'email', message: 'Email is invalid' });
    }

    // Mobile validation
    if (!formData.mobile_number) {
      newErrors.push({ field: 'mobile_number', message: 'Mobile number is required' });
    }

    // Date of birth validation
    if (!formData.dob) {
      newErrors.push({ field: 'dob', message: 'Date of birth is required' });
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.push({ field: 'gender', message: 'Gender is required' });
    }

    // Country validation
    if (!formData.country) {
      newErrors.push({ field: 'country', message: 'Country is required' });
    }

    // Password validation
    if (!formData.password) {
      newErrors.push({ field: 'password', message: 'Password is required' });
    } else if (!isValidPassword) {
      newErrors.push({ field: 'password', message: 'Password does not meet requirements' });
    }

    if (!formData.password_confirmation) {
      newErrors.push({ field: 'password_confirmation', message: 'Please confirm your password' });
    } else if (!passwordsMatch) {
      newErrors.push({ field: 'password_confirmation', message: 'Passwords do not match' });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    clearFieldError(field);
  };

  const handleCountryChange = (countryLabel: string) => {
    const selectedCountry = countries.find(c => c.label === countryLabel);
    setFormData(prev => ({
      ...prev,
      country: countryLabel,
      mobile_region_code: selectedCountry?.phone_code || '+60'
    }));
    clearFieldError('country');
    clearFieldError('mobile_region_code');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiMessage('');
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {

      const response = await api.post('/auth/register', {...formData, plan: 'free'});
      const result: SignUpResponse = response.data as SignUpResponse;

      if (result.status === "success") {
        setApiMessage('Registration successful! Please check your email for verification code.');
        
        // Redirect to OTP verification page
        setTimeout(() => {
          navigate('/verify-email', { 
            state: { 
              email: formData.email,
              reset_token: result.data?.reset_token,
              from: 'signup'
            }
          });
        }, 2000);
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.errors) {
          const validationErrors: ValidationError[] = [];
          Object.keys(errorData.errors).forEach(field => {
            errorData.errors[field].forEach((message: string) => {
              validationErrors.push({ field, message });
            });
          });
          setErrors(validationErrors);
        } else {
          setApiMessage(errorData.message || 'Registration failed. Please try again.');
        }
      } else if (error.message) {
        setApiMessage(error.message);
      } else {
        setApiMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (

    <>
      {/* Logo with badge */}
      <Link to={homePath} className="relative flex md:absolute md:left-24 md:top-24 text-center items-center justify-center content-center">
        <img
          src="/navbar-logo-latest-white.png"
          alt="SKILLSTURE Navbar Logo"
          className="h-16 md:h-20"
          onError={(e) => e.currentTarget.src = '/navbar-logo.png'}
        />
      </Link>
      <div className="login-container min-w-full flex-col">
        <h1 className="login-title">Sign Up</h1>
        <p className="login-subtitle">Create your Skillsture account to get started.</p>
        
        {apiMessage && (
          <div className={`message ${apiMessage.includes('success') ? 'message-success' : 'message-error'}`}>
            <div className="message-content">
              {apiMessage}
            </div>
          </div>
        )}

        <div className="login-card">        
          <form onSubmit={handleSubmit} className="login-form">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="first_name" className="form-label text-start">First Name</label>
                <input
                  id="first_name"
                  type="text"
                  className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                    getFieldError('first_name') ? 'input-error' : ''
                  }`}
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  placeholder="Your First Name"
                  required
                  disabled={isLoading}
                />
                {getFieldError('first_name') && (
                  <div className="error-text">{getFieldError('first_name')}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="last_name" className="form-label text-start">Last Name</label>
                <input
                  id="last_name"
                  type="text"
                  className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                    getFieldError('last_name') ? 'input-error' : ''
                  }`}
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  placeholder="Your Last Name"
                  required
                  disabled={isLoading}
                />
                {getFieldError('last_name') && (
                  <div className="error-text">{getFieldError('last_name')}</div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email" className="form-label text-start">Email</label>
              <input
                id="email"
                type="email"
                className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                  getFieldError('email') ? 'input-error' : ''
                }`}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="abc@gmail.com"
                required
                disabled={isLoading}
              />
              {getFieldError('email') && (
                <div className="error-text">{getFieldError('email')}</div>
              )}
            </div>

            {/* Country */}
            <div className="form-group">
              <label htmlFor="country" className="form-label text-start">Country</label>
              <select
                id="country"
                className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                  getFieldError('country') ? 'input-error' : ''
                }`}
                value={formData.country}
                onChange={(e) => handleCountryChange(e.target.value)}
                required
                disabled={isLoading || isLoadingCountries}
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.label}>
                    {country.label}
                  </option>
                ))}
              </select>
              {isLoadingCountries && (
                <div className="text-xs text-gray-400 mt-1">Loading countries...</div>
              )}
              {getFieldError('country') && (
                <div className="error-text">{getFieldError('country')}</div>
              )}
            </div>

            {/* Mobile Number */}
            <div className="form-group">
              <label htmlFor="mobile_number" className="form-label text-start">Mobile Number</label>
              <div className="flex gap-2">
                <select
                  value={formData.mobile_region_code}
                  onChange={(e) => handleInputChange('mobile_region_code', e.target.value)}
                  className="flex-auto w-1/5 form-input placeholder:text-gray-400 rounded-full bg-blue-950/80"
                  disabled={isLoading || !formData.country}
                >
                  {/* Show loading or placeholder when no country selected */}
                  {!formData.country ? (
                    <option value="">Select country first</option>
                  ) : (
                    <>
                      {countries.find(c => c.label === formData.country)?.phone_codes.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </>
                  )}
                </select>
                <input
                  id="mobile_number"
                  type="tel"
                  className={`flex-auto w-4/5 form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                    getFieldError('mobile_number') ? 'input-error' : ''
                  }`}
                  value={formData.mobile_number}
                  onChange={(e) => handleInputChange('mobile_number', e.target.value)}
                  placeholder="1234567899"
                  required
                  disabled={isLoading}
                />
              </div>
              {getFieldError('mobile_number') && (
                <div className="error-text">{getFieldError('mobile_number')}</div>
              )}
              {formData.country && (
                <div className="text-xs text-gray-400 mt-1">
                  Phone codes available for {formData.country}
                </div>
              )}
            </div>

            {/* Date of Birth and Gender */}
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="dob" className="form-label text-start">Date of Birth</label>
                <input
                  id="dob"
                  type="date"
                  className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                    getFieldError('dob') ? 'input-error' : ''
                  }`}
                  value={formData.dob}
                  onChange={(e) => handleInputChange('dob', e.target.value)}
                  required
                  disabled={isLoading}
                />
                {getFieldError('dob') && (
                  <div className="error-text">{getFieldError('dob')}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="gender" className="form-label text-start">Gender</label>
                <select
                  id="gender"
                  className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                    getFieldError('gender') ? 'input-error' : ''
                  }`}
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  required
                  disabled={isLoading}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  {/* <option value="other">Other</option> */}
                </select>
                {getFieldError('gender') && (
                  <div className="error-text">{getFieldError('gender')}</div>
                )}
              </div>
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password" className="form-label text-start">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                    getFieldError('password') ? 'input-error' : ''
                  } pr-10`}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Enter Password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors bg-transparent rounded-full"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              
              {/* Password Requirements */}
              {formData.password && (
                <div className="space-y-1 text-xs mt-2 ml-2">
                  <div className={`flex items-center ${passwordValidation.hasMinLength ? 'text-green-400' : 'text-red-400'}`}>
                    <span className="w-2 h-2 rounded-full mr-2 bg-current"></span>
                    contains at least 8 characters
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasUppercase ? 'text-green-400' : 'text-red-400'}`}>
                    <span className="w-2 h-2 rounded-full mr-2 bg-current"></span>
                    contains at least one capital letter
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasLowercase ? 'text-green-400' : 'text-red-400'}`}>
                    <span className="w-2 h-2 rounded-full mr-2 bg-current"></span>
                    contains at least one lowercase letter
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasNumber ? 'text-green-400' : 'text-red-400'}`}>
                    <span className="w-2 h-2 rounded-full mr-2 bg-current"></span>
                    contains at least one number
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasSpecialChar ? 'text-green-400' : 'text-red-400'}`}>
                    <span className="w-2 h-2 rounded-full mr-2 bg-current"></span>
                    contains at least one special character
                  </div>
                </div>
              )}
              {getFieldError('password') && (
                <div className="error-text">{getFieldError('password')}</div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label htmlFor="password_confirmation" className="form-label text-start">Confirm Password</label>
              <div className="relative">
                <input
                  id="password_confirmation"
                  type={showConfirmPassword ? "text" : "password"}
                  className={`form-input placeholder:text-gray-400 rounded-full bg-blue-950/80 ${
                    getFieldError('password_confirmation') ? 'input-error' : ''
                  } pr-10`}
                  value={formData.password_confirmation}
                  onChange={(e) => handleInputChange('password_confirmation', e.target.value)}
                  placeholder="Retype Password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors bg-transparent rounded-full"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {getFieldError('password_confirmation') && (
                <div className="error-text">{getFieldError('password_confirmation')}</div>
              )}
            </div>
            
            <button 
              type="submit" 
              className={`login-button rounded-full ${isLoading ? 'loading' : ''}`}
              disabled={isLoading || isLoadingCountries}
            >
              {isLoading ? 'Creating Account...' : 'Sign up now'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-orange-500 hover:text-orange-400 underline">
                Login
              </Link>
            </p>
            <p className="text-gray-400 text-xs mt-2">
              By signing up, you agree to our{' '}
              <a href={termsPath} target="_blank" className="text-orange-500 hover:text-orange-400 underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href={policyPath} target="_blank" className="text-orange-500 hover:text-orange-400 underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUpPage;